
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ImportResults } from "@/types/import";
import { AlertCircle, CheckCircle, Info, Trash2 } from "lucide-react";
import { useState } from "react";

interface ImportSummaryProps {
  results?: ImportResults;
  showDeletions?: boolean;
}

export function ImportSummary({ results, showDeletions }: ImportSummaryProps) {
  const [showAllErrors, setShowAllErrors] = useState(false);
  
  if (!results) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>No Results</AlertTitle>
        <AlertDescription>
          No import results available. Please start an import.
        </AlertDescription>
      </Alert>
    );
  }
  
  const { successful, failed, deleted, totalRows, errors, categories, deletions } = results;
  const hasErrors = errors && errors.length > 0;
  const displayErrors = showAllErrors ? errors : errors?.slice(0, 5);
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500 mr-2" />
            <div>
              <div className="text-xl font-bold text-green-700">{successful}</div>
              <div className="text-sm text-green-600">Quotes imported</div>
            </div>
          </div>
        </Card>
        
        {showDeletions && deleted && deleted > 0 && (
          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center">
              <Trash2 className="h-8 w-8 text-orange-500 mr-2" />
              <div>
                <div className="text-xl font-bold text-orange-700">{deleted}</div>
                <div className="text-sm text-orange-600">Quotes deleted</div>
              </div>
            </div>
          </Card>
        )}
        
        {failed > 0 && (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-center">
              <AlertCircle className="h-8 w-8 text-red-500 mr-2" />
              <div>
                <div className="text-xl font-bold text-red-700">{failed}</div>
                <div className="text-sm text-red-600">Errors</div>
              </div>
            </div>
          </Card>
        )}
        
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center">
            <Info className="h-8 w-8 text-blue-500 mr-2" />
            <div>
              <div className="text-xl font-bold text-blue-700">{totalRows}</div>
              <div className="text-sm text-blue-600">Total rows processed</div>
            </div>
          </div>
        </Card>
      </div>
      
      {hasErrors && (
        <Accordion type="single" collapsible>
          <AccordionItem value="errors">
            <AccordionTrigger className="text-red-600">
              {errors.length} Errors Occurred
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 mt-2">
                {displayErrors?.map((error, i) => (
                  <Alert key={i} variant="destructive" className="text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error in row {error.row}</AlertTitle>
                    <AlertDescription className="font-mono text-xs mt-1">
                      {error.content && <div className="truncate mb-1">{error.content}</div>}
                      {error.error}
                    </AlertDescription>
                  </Alert>
                ))}
                
                {errors.length > 5 && !showAllErrors && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowAllErrors(true)}
                    className="mt-2"
                  >
                    Show all {errors.length} errors
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      
      {showDeletions && deletions && deletions.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="deletions">
            <AccordionTrigger className="text-orange-600">
              {deletions.length} Quotes Deleted
            </AccordionTrigger>
            <AccordionContent>
              <div className="mt-2 space-y-2">
                {deletions.map((deletion, i) => (
                  <div key={i} className="p-2 border rounded text-sm flex items-start">
                    <Trash2 className="h-4 w-4 text-orange-500 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium">{deletion.content}</div>
                      <div className="text-xs text-muted-foreground">ID: {deletion.id}</div>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      
      {categories && categories.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="categories">
            <AccordionTrigger>
              Categories Summary
            </AccordionTrigger>
            <AccordionContent>
              <div className="mt-2 space-y-1">
                {categories.map((category, i) => (
                  <div key={i} className="flex items-center justify-between p-2 border-b">
                    <div className="flex items-center">
                      <span className="mr-2">{category.name}</span>
                      {category.isNew && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">New</Badge>
                      )}
                    </div>
                    <Badge variant="secondary">{category.count}</Badge>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      
      <Separator />
      
      <div className="text-center">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Start New Import
        </Button>
      </div>
    </div>
  );
}
